import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Snackbar,
    Alert,
    Chip,
    Card,
    CardContent
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';

const EmployeeCertifications = ({ employee }) => {
    const [certifications, setCertifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentCertification, setCurrentCertification] = useState(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState('success');
    const [alertMessage, setAlertMessage] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [certificationToDelete, setCertificationToDelete] = useState(null);
    const [pageSize, setPageSize] = useState(10);

    const [formData, setFormData] = useState({
        cert_name: '',
        certifying_body: '',
        license_number: '',
        issue_date: '',
        expiration_date: '',
        reminder_days_before: 30
    });

    useEffect(() => {
        if (employee?.emp_id) {
            fetchCertifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee]);

    const fetchCertifications = async () => {
        try {
            setLoading(true);
            const response = await axios.get(APIURL + '/employees/certifications', {
                params: { employee_id: employee.emp_id }
            });
            setCertifications(response.data.certifications || []);
        } catch (error) {
            console.error('Error fetching certifications:', error);
            showAlert('error', 'Failed to load certifications');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (certification = null) => {
        if (certification) {
            setEditMode(true);
            setCurrentCertification(certification);
            setFormData({
                cert_name: certification.cert_name || '',
                certifying_body: certification.certifying_body || '',
                license_number: certification.license_number || '',
                issue_date: certification.issue_date ? moment.utc(certification.issue_date).format('YYYY-MM-DD') : '',
                expiration_date: certification.expiration_date ? moment.utc(certification.expiration_date).format('YYYY-MM-DD') : '',
                reminder_days_before: certification.reminder_days_before || 30
            });
        } else {
            setEditMode(false);
            setCurrentCertification(null);
            setFormData({
                cert_name: '',
                certifying_body: '',
                license_number: '',
                issue_date: '',
                expiration_date: '',
                reminder_days_before: 30
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditMode(false);
        setCurrentCertification(null);
        setFormData({
            cert_name: '',
            certifying_body: '',
            license_number: '',
            issue_date: '',
            expiration_date: '',
            reminder_days_before: 30
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveCertification = async () => {
        try {
            if (!formData.cert_name.trim()) {
                showAlert('error', 'Certification name is required');
                return;
            }

            const payload = {
                ...formData,
                issue_date: formData.issue_date || null,
                expiration_date: formData.expiration_date || null
            };

            if (editMode) {
                // Update existing certification
                await axios.put(APIURL + '/employees/certifications', {
                    certification_id: currentCertification.certification_id,
                    ...payload
                });
                showAlert('success', 'Certification updated successfully');
            } else {
                // Add new certification
                await axios.post(APIURL + '/employees/certifications', {
                    employee_id: employee.emp_id,
                    ...payload
                });
                showAlert('success', 'Certification added successfully');
            }

            handleCloseDialog();
            fetchCertifications();
        } catch (error) {
            console.error('Error saving certification:', error);
            showAlert('error', 'Failed to save certification');
        }
    };

    const handleDeleteClick = (certification) => {
        setCertificationToDelete(certification);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(APIURL + '/employees/certifications', {
                data: {
                    certification_id: certificationToDelete.certification_id
                }
            });
            showAlert('success', 'Certification deleted successfully');
            setDeleteDialogOpen(false);
            setCertificationToDelete(null);
            fetchCertifications();
        } catch (error) {
            console.error('Error deleting certification:', error);
            showAlert('error', 'Failed to delete certification');
        }
    };

    const showAlert = (severity, message) => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Expired':
                return 'error';
            case 'Expiring Soon':
                return 'warning';
            case 'Active':
                return 'success';
            default:
                return 'default';
        }
    };

    const columns = [
        {
            field: 'cert_name',
            headerName: 'CERTIFICATION',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'certifying_body',
            headerName: 'CERTIFYING BODY',
            width: 180
        },
        {
            field: 'license_number',
            headerName: 'LICENSE #',
            width: 130
        },
        {
            field: 'issue_date',
            headerName: 'ISSUED',
            width: 110,
            valueFormatter: (value) => {
                return value ? moment.utc(value).format('MM/DD/YYYY') : '-';
            }
        },
        {
            field: 'expiration_date',
            headerName: 'EXPIRES',
            width: 110,
            valueFormatter: (value) => {
                return value ? moment.utc(value).format('MM/DD/YYYY') : 'No Expiry';
            }
        },
        {
            field: 'status',
            headerName: 'STATUS',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value || 'Active'}
                    color={getStatusColor(params.value)}
                    size="small"
                    icon={params.value === 'Expiring Soon' || params.value === 'Expired' ? <WarningIcon /> : undefined}
                />
            )
        },
        {
            field: 'actions',
            headerName: 'ACTIONS',
            width: 120,
            renderCell: (params) => (
                <Box>
                    <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(params.row)}
                        color="primary"
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(params.row)}
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )
        }
    ];

    const rows = certifications.map((cert) => ({
        id: cert.certification_id,
        ...cert
    }));

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                    Certifications for {employee?.emp_first_name} {employee?.emp_last_name}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Certification
                </Button>
            </Box>

            {certifications.length === 0 && !loading ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="textSecondary" align="center">
                            No certifications recorded for this employee.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    pageSizeOptions={[10, 25, 50]}
                    pagination
                    autoHeight
                    disableSelectionOnClick
                    initialState={{ density: 'compact' }}
                />
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editMode ? 'Edit Certification' : 'Add Certification'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Certification Name"
                            value={formData.cert_name}
                            onChange={(e) => handleInputChange('cert_name', e.target.value)}
                            fullWidth
                            required
                            inputProps={{ maxLength: 200 }}
                        />

                        <TextField
                            label="Certifying Body"
                            value={formData.certifying_body}
                            onChange={(e) => handleInputChange('certifying_body', e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 200 }}
                        />

                        <TextField
                            label="License Number"
                            value={formData.license_number}
                            onChange={(e) => handleInputChange('license_number', e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 100 }}
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Issue Date"
                                type="date"
                                value={formData.issue_date}
                                onChange={(e) => handleInputChange('issue_date', e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                label="Expiration Date"
                                type="date"
                                value={formData.expiration_date}
                                onChange={(e) => handleInputChange('expiration_date', e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText="Leave empty if no expiry"
                            />
                        </Box>

                        <TextField
                            label="Reminder Days Before Expiration"
                            type="number"
                            value={formData.reminder_days_before}
                            onChange={(e) => handleInputChange('reminder_days_before', e.target.value)}
                            fullWidth
                            inputProps={{ min: 0 }}
                            helperText="Number of days before expiration to send reminder (default: 30)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSaveCertification} variant="contained">
                        {editMode ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the certification{' '}
                        <strong>{certificationToDelete?.cert_name}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar
                open={alertOpen}
                autoHideDuration={5000}
                onClose={() => setAlertOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setAlertOpen(false)}
                    severity={alertSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EmployeeCertifications;
